import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import {
  ArrowRight,
  Calendar,
  Clock,
  CheckCircle,
  Video,
  Users,
  Shield,
  Loader2,
  ShieldCheck,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const timeSlots = [
  '09:00 AM',
  '10:00 AM',
  '11:00 AM',
  '02:00 PM',
  '03:00 PM',
  '04:00 PM',
];

const companySizes = [
  '1-50 employees',
  '51-200 employees',
  '201-1000 employees',
  '1000+ employees',
];

const interests = [
  'Sustainability Data Engineering',
  'Reporting & Compliance',
  'dMRV Solution',
  'Tokenization & Blockchain',
  'Credibility Engine',
  'General Consultation',
];

const benefits = [
  {
    icon: Video,
    title: '30-Minute Video Call',
    description: 'A focused conversation with our solution experts.',
  },
  {
    icon: Users,
    title: 'Tailored Discussion',
    description: 'We will focus on your specific industry and challenges.',
  },
  {
    icon: Shield,
    title: 'No Commitment',
    description: 'Exploratory call with no obligation.',
  },
];

// Simple math CAPTCHA generator
function generateCaptcha() {
  const num1 = Math.floor(Math.random() * 10) + 1;
  const num2 = Math.floor(Math.random() * 10) + 1;
  return {
    question: `What is ${num1} + ${num2}?`,
    answer: num1 + num2,
  };
}

export const BookCall = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    companySize: '',
    interest: '',
    date: '',
    time: '',
    additionalInfo: '',
    captchaAnswer: '',
    honeypot: '',
  });
  const [captcha, setCaptcha] = useState(generateCaptcha());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    setCaptcha(generateCaptcha());
  }, []);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNext = () => {
    if (step === 1) {
      if (!formData.name || !formData.email || !formData.company) {
        toast.error('Please fill in all required fields.');
        return;
      }
    }
    setStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setStep((prev) => prev - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.date || !formData.time) {
      toast.error('Please select a date and time.');
      return;
    }

    // Bot detection
    if (formData.honeypot) {
      toast.error('Bot detected. Please try again.');
      return;
    }

    // Verify CAPTCHA
    if (parseInt(formData.captchaAnswer) !== captcha.answer) {
      toast.error('Incorrect answer. Please try again.');
      setCaptcha(generateCaptcha());
      setFormData((prev) => ({ ...prev, captchaAnswer: '' }));
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/book-call`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company,
          company_size: formData.companySize || null,
          interest: formData.interest || 'General Consultation',
          preferred_date: formData.date || null,
          preferred_time: formData.time || null,
          additional_info: formData.additionalInfo || null,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      setIsSubmitting(false);
      setIsSubmitted(true);
      toast.success('Discovery call booked successfully!');
    } catch (error) {
      console.error('Form submission error:', error);
      setIsSubmitting(false);
      toast.error('Failed to book call. Please try again.');
      setCaptcha(generateCaptcha());
    }
  };

  // Generate next 14 days for date selection
  const getAvailableDates = () => {
    const dates = [];
    const today = new Date();
    for (let i = 1; i <= 14; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      // Skip weekends
      if (date.getDay() !== 0 && date.getDay() !== 6) {
        dates.push({
          value: date.toISOString().split('T')[0],
          label: date.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
          }),
        });
      }
    }
    return dates;
  };

  const availableDates = getAvailableDates();

  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <Badge variant="outline" className="mb-4 border-teal/30 text-teal bg-teal/10">
              Discovery Call
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Book a Discovery Call
            </h1>
            <p className="text-xl text-white/70">
              Schedule a 30-minute conversation with our team to explore how SusTek
              can help transform your sustainability data program.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-12 bg-background border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-teal/10 text-teal flex items-center justify-center flex-shrink-0">
                  <benefit.icon className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">{benefit.title}</h3>
                  <p className="text-sm text-muted-foreground">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Form */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-border/50 bg-card">
            <CardHeader>
              <div className="flex items-center justify-between mb-4">
                <div className="flex gap-2">
                  {[1, 2, 3].map((s) => (
                    <div
                      key={s}
                      className={`w-10 h-1 rounded-full transition-colors ${
                        s <= step ? 'bg-teal' : 'bg-muted'
                      }`}
                    />
                  ))}
                </div>
                <span className="text-sm text-muted-foreground">Step {step} of 3</span>
              </div>
              <CardTitle>
                {step === 1 && 'Your Information'}
                {step === 2 && 'Your Interest'}
                {step === 3 && 'Select a Time'}
              </CardTitle>
              <CardDescription>
                {step === 1 && 'Tell us about yourself and your organization.'}
                {step === 2 && 'What would you like to discuss?'}
                {step === 3 && 'Choose a convenient date and time.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="h-10 w-10 text-success" />
                  </div>
                  <h3 className="text-2xl font-semibold text-foreground mb-2">
                    Call Booked!
                  </h3>
                  <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                    You are scheduled for{' '}
                    <span className="font-medium text-foreground">
                      {availableDates.find((d) => d.value === formData.date)?.label}
                    </span>{' '}
                    at{' '}
                    <span className="font-medium text-foreground">{formData.time}</span>.
                    <br />
                    We have sent a calendar invite to{' '}
                    <span className="font-medium text-foreground">{formData.email}</span>.
                  </p>
                  <div className="p-4 rounded-lg bg-teal/5 border border-teal/20 max-w-md mx-auto">
                    <p className="text-sm text-muted-foreground">
                      <strong className="text-foreground">What is next?</strong>
                      <br />
                      Check your email for the meeting link. Feel free to reply with any
                      materials or questions you would like to discuss.
                    </p>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  {/* Honeypot field */}
                  <input
                    type="text"
                    name="website"
                    value={formData.honeypot}
                    onChange={(e) => handleChange('honeypot', e.target.value)}
                    style={{ display: 'none' }}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                  
                  {/* Step 1: Basic Info */}
                  {step === 1 && (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          placeholder="John Smith"
                          value={formData.name}
                          onChange={(e) => handleChange('name', e.target.value)}
                          required
                          disabled={isSubmitting}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Work Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="john@company.com"
                          value={formData.email}
                          onChange={(e) => handleChange('email', e.target.value)}
                          required
                          disabled={isSubmitting}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="company">Company Name *</Label>
                        <Input
                          id="company"
                          placeholder="Company Name"
                          value={formData.company}
                          onChange={(e) => handleChange('company', e.target.value)}
                          required
                          disabled={isSubmitting}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="companySize">Company Size</Label>
                        <Select
                          value={formData.companySize}
                          onValueChange={(value) => handleChange('companySize', value)}
                          disabled={isSubmitting}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select size" />
                          </SelectTrigger>
                          <SelectContent>
                            {companySizes.map((size) => (
                              <SelectItem key={size} value={size}>
                                {size}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}

                  {/* Step 2: Interest */}
                  {step === 2 && (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>What are you interested in? *</Label>
                        <RadioGroup
                          value={formData.interest}
                          onValueChange={(value) => handleChange('interest', value)}
                          className="space-y-3"
                          disabled={isSubmitting}
                        >
                          {interests.map((interest) => (
                            <div
                              key={interest}
                              className="flex items-center space-x-3 p-3 rounded-lg border border-border hover:border-teal/50 transition-colors"
                            >
                              <RadioGroupItem value={interest} id={interest} />
                              <Label htmlFor={interest} className="cursor-pointer flex-1">
                                {interest}
                              </Label>
                            </div>
                          ))}
                        </RadioGroup>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="additionalInfo">
                          Anything else you would like us to know? (Optional)
                        </Label>
                        <Textarea
                          id="additionalInfo"
                          placeholder="Tell us about your current challenges or goals..."
                          rows={4}
                          value={formData.additionalInfo}
                          onChange={(e) => handleChange('additionalInfo', e.target.value)}
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>
                  )}

                  {/* Step 3: Schedule */}
                  {step === 3 && (
                    <div className="space-y-6">
                      <div className="space-y-3">
                        <Label className="flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          Select a Date *
                        </Label>
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                          {availableDates.map((date) => (
                            <button
                              key={date.value}
                              type="button"
                              onClick={() => handleChange('date', date.value)}
                              disabled={isSubmitting}
                              className={`p-3 rounded-lg border text-sm transition-colors ${
                                formData.date === date.value
                                  ? 'border-teal bg-teal/10 text-teal font-medium'
                                  : 'border-border hover:border-teal/50'
                              } disabled:opacity-50`}
                            >
                              {date.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-3">
                        <Label className="flex items-center gap-2">
                          <Clock className="h-4 w-4" />
                          Select a Time (SGT) *
                        </Label>
                        <div className="grid grid-cols-3 gap-2">
                          {timeSlots.map((time) => (
                            <button
                              key={time}
                              type="button"
                              onClick={() => handleChange('time', time)}
                              disabled={isSubmitting}
                              className={`p-3 rounded-lg border text-sm transition-colors ${
                                formData.time === time
                                  ? 'border-teal bg-teal/10 text-teal font-medium'
                                  : 'border-border hover:border-teal/50'
                              } disabled:opacity-50`}
                            >
                              {time}
                            </button>
                          ))}
                        </div>
                      </div>
                      
                      {/* CAPTCHA */}
                      <div className="p-4 rounded-lg bg-muted/50 border border-border">
                        <div className="flex items-center gap-2 mb-3">
                          <ShieldCheck className="h-5 w-5 text-teal" />
                          <Label className="text-sm font-medium">Security Verification</Label>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-sm text-muted-foreground">{captcha.question}</span>
                          <Input
                            type="number"
                            placeholder="Your answer"
                            value={formData.captchaAnswer}
                            onChange={(e) => handleChange('captchaAnswer', e.target.value)}
                            required
                            disabled={isSubmitting}
                            className="w-32"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Navigation */}
                  <div className="flex justify-between mt-8 pt-6 border-t border-border">
                    {step > 1 && (
                      <Button type="button" variant="outline" onClick={handleBack} disabled={isSubmitting}>
                        Back
                      </Button>
                    )}
                    {step < 3 ? (
                      <Button
                        type="button"
                        variant="teal"
                        onClick={handleNext}
                        className="ml-auto group"
                        disabled={isSubmitting}
                      >
                        Continue
                        <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    ) : (
                      <Button
                        type="submit"
                        variant="teal"
                        className="ml-auto"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Booking...
                          </>
                        ) : (
                          'Book Discovery Call'
                        )}
                      </Button>
                    )}
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default BookCall;
